import * as XLSX from 'xlsx';
import { normalizeClientRecord } from '../lib/clientUtils';

const HEADER_ALIASES = {
  name: ['name', 'client', 'clientname', 'customer', 'customername', 'full_name', 'fullname'],
  firstName: ['firstname', 'first_name', 'givenname', 'given_name'],
  lastName: ['lastname', 'last_name', 'surname', 'familyname', 'family_name'],
  email: ['email', 'emailaddress', 'e-mail', 'mail'],
  phone: ['phone', 'phonenumber', 'mobile', 'mobilephone', 'telephone', 'tel'],
  notes: ['notes', 'note', 'comment', 'comments', 'memo'],
  tags: ['tags', 'tag', 'segment', 'segments', 'group']
};

const FALLBACK_COLUMN_MAP = {
  name: 0,
  email: 1,
  phone: 2,
  notes: 3
};

const normalizeHeaderKey = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

const hasMeaningfulValue = (value) =>
  value !== null &&
  value !== undefined &&
  String(value).trim() !== '';

const buildColumnMap = (headerRow = []) => {
  const normalizedHeaders = headerRow.map((cell) => normalizeHeaderKey(cell));
  const map = {};

  Object.entries(HEADER_ALIASES).forEach(([field, aliases]) => {
    const index = normalizedHeaders.findIndex((header) => aliases.includes(header));
    if (index >= 0) {
      map[field] = index;
    }
  });

  return map;
};

const extractCellValue = (row, index) => {
  if (!Array.isArray(row) || !Number.isInteger(index) || index < 0) return '';
  return row[index] ?? '';
};

export const parseClientSpreadsheet = async (file) => {
  if (!file) {
    throw new Error('No spreadsheet file provided');
  }

  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array' });
  const firstSheetName = workbook.SheetNames[0];

  if (!firstSheetName) {
    return {
      clients: [],
      skippedRows: 0,
      fileName: file.name || ''
    };
  }

  const sheet = workbook.Sheets[firstSheetName];
  const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
  const nonEmptyRows = rows.filter((row) =>
    Array.isArray(row) && row.some((cell) => hasMeaningfulValue(cell))
  );

  if (nonEmptyRows.length === 0) {
    return {
      clients: [],
      skippedRows: 0,
      fileName: file.name || ''
    };
  }

  const detectedColumnMap = buildColumnMap(nonEmptyRows[0]);
  const hasDetectedHeaders = Object.keys(detectedColumnMap).length > 0;
  const dataRows = hasDetectedHeaders ? nonEmptyRows.slice(1) : nonEmptyRows;
  const columnMap = hasDetectedHeaders ? detectedColumnMap : FALLBACK_COLUMN_MAP;

  const clients = [];
  let skippedRows = 0;

  dataRows.forEach((row) => {
    const rawClient = {
      name: extractCellValue(row, columnMap.name),
      firstName: extractCellValue(row, columnMap.firstName),
      lastName: extractCellValue(row, columnMap.lastName),
      email: extractCellValue(row, columnMap.email),
      phone: extractCellValue(row, columnMap.phone),
      notes: extractCellValue(row, columnMap.notes),
      tags: extractCellValue(row, columnMap.tags)
    };

    if (
      !hasMeaningfulValue(rawClient.name) &&
      !hasMeaningfulValue(rawClient.firstName) &&
      !hasMeaningfulValue(rawClient.lastName) &&
      !hasMeaningfulValue(rawClient.email) &&
      !hasMeaningfulValue(rawClient.phone)
    ) {
      skippedRows += 1;
      return;
    }

    clients.push(normalizeClientRecord(rawClient));
  });

  return {
    clients,
    skippedRows,
    fileName: file.name || '',
    detectedHeaders: hasDetectedHeaders
  };
};
